/**
 * MANAGEMENT (14) / ONBOARDING-REQUESTS — the review-workflow i18n (FR default + AR-RTL). ADDITIVE to the
 * shell `../messages.ts` + the sibling `./onboarding.messages.ts` (the lab directory/provision strings). Every
 * key is namespaced `mgmt.reqs.*` so it never shadows a shell/onboarding key (registerMessages is
 * Object.assign). Idempotent, self-contained: `registerOnboardingRequestsMessages()` runs once at the
 * screen's module-load — no shell hookup needed (the per-namespace i18n idiom).
 *
 * The email-template previews (`mgmt.reqs.mail.*`) mirror the FOUR server EmailTemplate codes the
 * onboardingRequests.service queues on each transition (onboarding_demande_recue / _en_revue / _approuvee /
 * _rejetee). The operator sees WHAT the prospect will receive before confirming a transition — the transition
 * IS the send (the server queues the templated mail on commit); no separate "send" endpoint exists.
 */
import { registerMessages } from "@/app";

const FR: Record<string, string> = {
  // nav (also declared in the shell messages via the returned Integrate hookup so the side-nav label
  // resolves before this screen's chunk loads; kept here too so the screen is self-contained)
  "nav.mgmt.requests": "Demandes d'accès",
  // page header + tabs
  "mgmt.reqs.title": "Demandes d'accès",
  "mgmt.reqs.subtitle": "Le tunnel d'inscription des laboratoires — de la réception à l'approbation.",
  "mgmt.reqs.tab.inbox": "Demandes",
  "mgmt.reqs.tab.directory": "Laboratoires",

  // inbox filters
  "mgmt.reqs.search": "Rechercher (labo, email, référence…)",
  "mgmt.reqs.filter.statut": "Statut",
  "mgmt.reqs.filter.all": "Tous",
  "mgmt.reqs.filter.wilaya": "Filtrer par wilaya",

  // statut labels + funnel strip
  "mgmt.reqs.statut.demande": "Nouvelle",
  "mgmt.reqs.statut.en_revue": "En revue",
  "mgmt.reqs.statut.approuvee": "Approuvée",
  "mgmt.reqs.statut.rejetee": "Rejetée",
  "mgmt.reqs.flow.title": "Répartition du tunnel (page courante)",
  "mgmt.reqs.flow.aria": "Répartition des demandes d'accès par étape du tunnel",
  "mgmt.reqs.flow.empty": "Aucune demande à analyser sur cette page.",
  "mgmt.reqs.flow.pending": "À traiter",

  // inbox columns
  "mgmt.reqs.col.reference": "Référence",
  "mgmt.reqs.col.lab": "Laboratoire",
  "mgmt.reqs.col.contact": "Contact",
  "mgmt.reqs.col.wilaya": "Wilaya",
  "mgmt.reqs.col.statut": "Statut",
  "mgmt.reqs.col.received": "Reçue le",
  "mgmt.reqs.col.actions": "Actions",
  "mgmt.reqs.action.review": "Examiner",

  // empty states
  "mgmt.reqs.empty.title": "Aucune demande d'accès",
  "mgmt.reqs.empty.desc": "Les demandes d'inscription soumises depuis le portail public apparaîtront ici.",
  "mgmt.reqs.empty.filtered": "Aucune demande ne correspond aux filtres.",

  // review drawer — dossier
  "mgmt.reqs.drawer.title": "Dossier de la demande",
  "mgmt.reqs.drawer.dossier": "Détails de la demande",
  "mgmt.reqs.field.reference": "Référence",
  "mgmt.reqs.field.lab": "Laboratoire",
  "mgmt.reqs.field.wilaya": "Wilaya",
  "mgmt.reqs.field.email": "Email",
  "mgmt.reqs.field.telephone": "Téléphone",
  "mgmt.reqs.field.received": "Reçue le",
  "mgmt.reqs.field.message": "Message du demandeur",
  "mgmt.reqs.field.reviewedAt": "Traitée le",
  "mgmt.reqs.field.raisonRejet": "Motif du rejet",
  "mgmt.reqs.field.provisioned": "Laboratoire provisionné",
  "mgmt.reqs.field.none": "—",

  // review drawer — decision panel
  "mgmt.reqs.decision.title": "Décision",
  "mgmt.reqs.decision.note": "Note interne (journalisée)",
  "mgmt.reqs.decision.note.placeholder": "Optionnel — tracé dans le journal d'audit",
  "mgmt.reqs.decision.terminal": "Cette demande est clôturée. Aucune action supplémentaire n'est possible.",
  "mgmt.reqs.action.take": "Prendre en revue",
  "mgmt.reqs.action.approve": "Approuver",
  "mgmt.reqs.action.reject": "Rejeter",
  "mgmt.reqs.action.cancel": "Annuler",
  "mgmt.reqs.action.close": "Fermer",
  "mgmt.reqs.action.back": "Retour",

  // review drawer — reject form
  "mgmt.reqs.reject.title": "Rejeter la demande",
  "mgmt.reqs.reject.reason": "Motif du rejet",
  "mgmt.reqs.reject.reason.placeholder": "Expliquez pourquoi la demande est refusée (envoyé au demandeur).",
  "mgmt.reqs.reject.reason.required": "Le motif du rejet est obligatoire.",
  "mgmt.reqs.reject.submit": "Confirmer le rejet",

  // review drawer — approve / provision form
  "mgmt.reqs.approve.title": "Approuver et provisionner",
  "mgmt.reqs.approve.intro": "Renseignez les champs de gouvernance pour créer le laboratoire et son compte propriétaire.",
  "mgmt.reqs.approve.lab": "Laboratoire",
  "mgmt.reqs.approve.owner": "Propriétaire (premier compte)",
  "mgmt.reqs.approve.reference": "Code laboratoire (Pharmakon)",
  "mgmt.reqs.approve.raisonSociale": "Raison sociale",
  "mgmt.reqs.approve.nomCommercial": "Nom commercial",
  "mgmt.reqs.approve.deployClass": "Classe de déploiement",
  "mgmt.reqs.approve.tenancyTier": "Mode de tenance",
  "mgmt.reqs.approve.tenancy.partage": "Partagé",
  "mgmt.reqs.approve.tenancy.dedie": "Dédié",
  "mgmt.reqs.approve.owner.prenom": "Prénom",
  "mgmt.reqs.approve.owner.nom": "Nom",
  "mgmt.reqs.approve.owner.email": "Email du propriétaire",
  "mgmt.reqs.approve.owner.password": "Mot de passe initial",
  "mgmt.reqs.approve.submit": "Provisionner et approuver",

  // email-template preview (mirrors the four server templates queued on each transition)
  "mgmt.reqs.mail.preview": "Aperçu de l'email au demandeur",
  "mgmt.reqs.mail.to": "À",
  "mgmt.reqs.mail.subject": "Objet",
  "mgmt.reqs.mail.hint": "Cet email sera envoyé automatiquement lors de la validation de cette étape.",
  "mgmt.reqs.mail.recue.subject": "Nous avons bien reçu votre demande — {reference}",
  "mgmt.reqs.mail.recue.body":
    "Bonjour,\n\nNous avons bien reçu votre demande d'accès à Novanalyse pour « {nomLabo} » (wilaya {wilaya}). Votre référence de suivi est {reference}. Notre équipe l'examinera prochainement.\n\nL'équipe Pharmakon.",
  "mgmt.reqs.mail.en_revue.subject": "Votre demande {reference} est en cours d'examen",
  "mgmt.reqs.mail.en_revue.body":
    "Bonjour,\n\nVotre demande d'accès pour « {nomLabo} » (référence {reference}) est actuellement en cours d'examen par notre équipe. Nous reviendrons vers vous très prochainement.\n\nL'équipe Pharmakon.",
  "mgmt.reqs.mail.approuvee.subject": "Bienvenue sur Novanalyse — votre laboratoire est prêt",
  "mgmt.reqs.mail.approuvee.body":
    "Bonjour,\n\nVotre demande pour « {nomLabo} » (référence {reference}) a été approuvée. Votre laboratoire a été créé et le compte propriétaire vous permettra de vous connecter. Vous recevrez vos accès séparément.\n\nL'équipe Pharmakon.",
  "mgmt.reqs.mail.rejetee.subject": "Suite à votre demande d'accès {reference}",
  "mgmt.reqs.mail.rejetee.body":
    "Bonjour,\n\nAprès examen, nous ne pouvons donner suite à votre demande d'accès pour « {nomLabo} » (référence {reference}).\n\nMotif : {raisonRejet}\n\nL'équipe Pharmakon.",

  // toasts + feedback
  "mgmt.reqs.toast.reviewed": "Demande prise en revue.",
  "mgmt.reqs.toast.approved": "Demande approuvée — laboratoire « {name} » provisionné.",
  "mgmt.reqs.toast.rejected": "Demande rejetée.",
  "mgmt.reqs.field.required": "Tous les champs obligatoires (*) doivent être renseignés.",
  "mgmt.reqs.field.invalidEmail": "Adresse email invalide.",
  "mgmt.reqs.error.generic": "Une erreur est survenue. Réessayez.",
};

const AR: Record<string, string> = {
  "nav.mgmt.requests": "طلبات الوصول",
  "mgmt.reqs.title": "طلبات الوصول",
  "mgmt.reqs.subtitle": "مسار تسجيل المختبرات — من الاستلام إلى الموافقة.",
  "mgmt.reqs.tab.inbox": "الطلبات",
  "mgmt.reqs.tab.directory": "المختبرات",

  "mgmt.reqs.search": "بحث (المختبر، البريد، المرجع…)",
  "mgmt.reqs.filter.statut": "الحالة",
  "mgmt.reqs.filter.all": "الكل",
  "mgmt.reqs.filter.wilaya": "تصفية حسب الولاية",

  "mgmt.reqs.statut.demande": "جديدة",
  "mgmt.reqs.statut.en_revue": "قيد المراجعة",
  "mgmt.reqs.statut.approuvee": "مقبولة",
  "mgmt.reqs.statut.rejetee": "مرفوضة",
  "mgmt.reqs.flow.title": "توزيع المسار (الصفحة الحالية)",
  "mgmt.reqs.flow.aria": "توزيع طلبات الوصول حسب مرحلة المسار",
  "mgmt.reqs.flow.empty": "لا توجد طلبات للتحليل في هذه الصفحة.",
  "mgmt.reqs.flow.pending": "بانتظار المعالجة",

  "mgmt.reqs.col.reference": "المرجع",
  "mgmt.reqs.col.lab": "المختبر",
  "mgmt.reqs.col.contact": "الاتصال",
  "mgmt.reqs.col.wilaya": "الولاية",
  "mgmt.reqs.col.statut": "الحالة",
  "mgmt.reqs.col.received": "استُلمت في",
  "mgmt.reqs.col.actions": "إجراءات",
  "mgmt.reqs.action.review": "مراجعة",

  "mgmt.reqs.empty.title": "لا توجد طلبات وصول",
  "mgmt.reqs.empty.desc": "ستظهر هنا طلبات التسجيل المرسَلة من البوابة العمومية.",
  "mgmt.reqs.empty.filtered": "لا يوجد طلب يطابق عوامل التصفية.",

  "mgmt.reqs.drawer.title": "ملف الطلب",
  "mgmt.reqs.drawer.dossier": "تفاصيل الطلب",
  "mgmt.reqs.field.reference": "المرجع",
  "mgmt.reqs.field.lab": "المختبر",
  "mgmt.reqs.field.wilaya": "الولاية",
  "mgmt.reqs.field.email": "البريد الإلكتروني",
  "mgmt.reqs.field.telephone": "الهاتف",
  "mgmt.reqs.field.received": "استُلمت في",
  "mgmt.reqs.field.message": "رسالة مقدّم الطلب",
  "mgmt.reqs.field.reviewedAt": "عُولجت في",
  "mgmt.reqs.field.raisonRejet": "سبب الرفض",
  "mgmt.reqs.field.provisioned": "المختبر المُجهَّز",
  "mgmt.reqs.field.none": "—",

  "mgmt.reqs.decision.title": "القرار",
  "mgmt.reqs.decision.note": "ملاحظة داخلية (مسجَّلة)",
  "mgmt.reqs.decision.note.placeholder": "اختياري — تُسجَّل في سجل التدقيق",
  "mgmt.reqs.decision.terminal": "هذا الطلب مغلق. لا يمكن اتخاذ أي إجراء إضافي.",
  "mgmt.reqs.action.take": "بدء المراجعة",
  "mgmt.reqs.action.approve": "قبول",
  "mgmt.reqs.action.reject": "رفض",
  "mgmt.reqs.action.cancel": "إلغاء",
  "mgmt.reqs.action.close": "إغلاق",
  "mgmt.reqs.action.back": "رجوع",

  "mgmt.reqs.reject.title": "رفض الطلب",
  "mgmt.reqs.reject.reason": "سبب الرفض",
  "mgmt.reqs.reject.reason.placeholder": "اشرح سبب رفض الطلب (يُرسَل إلى مقدّم الطلب).",
  "mgmt.reqs.reject.reason.required": "سبب الرفض إلزامي.",
  "mgmt.reqs.reject.submit": "تأكيد الرفض",

  "mgmt.reqs.approve.title": "القبول والتجهيز",
  "mgmt.reqs.approve.intro": "املأ حقول الحوكمة لإنشاء المختبر وحساب مالكه.",
  "mgmt.reqs.approve.lab": "المختبر",
  "mgmt.reqs.approve.owner": "المالك (الحساب الأول)",
  "mgmt.reqs.approve.reference": "رمز المختبر (فارماكون)",
  "mgmt.reqs.approve.raisonSociale": "الاسم القانوني",
  "mgmt.reqs.approve.nomCommercial": "الاسم التجاري",
  "mgmt.reqs.approve.deployClass": "فئة النشر",
  "mgmt.reqs.approve.tenancyTier": "نمط الاستضافة",
  "mgmt.reqs.approve.tenancy.partage": "مشترك",
  "mgmt.reqs.approve.tenancy.dedie": "مخصص",
  "mgmt.reqs.approve.owner.prenom": "الاسم",
  "mgmt.reqs.approve.owner.nom": "اللقب",
  "mgmt.reqs.approve.owner.email": "البريد الإلكتروني للمالك",
  "mgmt.reqs.approve.owner.password": "كلمة المرور الأولية",
  "mgmt.reqs.approve.submit": "التجهيز والقبول",

  "mgmt.reqs.mail.preview": "معاينة البريد إلى مقدّم الطلب",
  "mgmt.reqs.mail.to": "إلى",
  "mgmt.reqs.mail.subject": "الموضوع",
  "mgmt.reqs.mail.hint": "سيُرسَل هذا البريد تلقائيًا عند تأكيد هذه المرحلة.",
  "mgmt.reqs.mail.recue.subject": "لقد استلمنا طلبك — {reference}",
  "mgmt.reqs.mail.recue.body":
    "مرحبًا،\n\nلقد استلمنا طلب وصولك إلى نوفانالِز لـ « {nomLabo} » (ولاية {wilaya}). مرجع المتابعة هو {reference}. سيراجعه فريقنا قريبًا.\n\nفريق فارماكون.",
  "mgmt.reqs.mail.en_revue.subject": "طلبك {reference} قيد المراجعة",
  "mgmt.reqs.mail.en_revue.body":
    "مرحبًا،\n\nطلب وصولك لـ « {nomLabo} » (المرجع {reference}) قيد المراجعة حاليًا من قِبل فريقنا. سنعود إليك قريبًا.\n\nفريق فارماكون.",
  "mgmt.reqs.mail.approuvee.subject": "مرحبًا بك في نوفانالِز — مختبرك جاهز",
  "mgmt.reqs.mail.approuvee.body":
    "مرحبًا،\n\nتمت الموافقة على طلبك لـ « {nomLabo} » (المرجع {reference}). أُنشئ مختبرك وسيتيح لك حساب المالك تسجيل الدخول. ستتلقى بيانات الدخول بشكل منفصل.\n\nفريق فارماكون.",
  "mgmt.reqs.mail.rejetee.subject": "بخصوص طلب وصولك {reference}",
  "mgmt.reqs.mail.rejetee.body":
    "مرحبًا،\n\nبعد المراجعة، لا يمكننا قبول طلب وصولك لـ « {nomLabo} » (المرجع {reference}).\n\nالسبب: {raisonRejet}\n\nفريق فارماكون.",

  "mgmt.reqs.toast.reviewed": "تم أخذ الطلب قيد المراجعة.",
  "mgmt.reqs.toast.approved": "تمت الموافقة على الطلب — جُهِّز المختبر « {name} ».",
  "mgmt.reqs.toast.rejected": "تم رفض الطلب.",
  "mgmt.reqs.field.required": "يجب ملء جميع الحقول الإلزامية (*).",
  "mgmt.reqs.field.invalidEmail": "عنوان بريد إلكتروني غير صالح.",
  "mgmt.reqs.error.generic": "حدث خطأ. أعد المحاولة.",
};

let registered = false;
/** Register the review-workflow i18n (idempotent). Called at the screen's module-load. */
export function registerOnboardingRequestsMessages(): void {
  if (registered) return;
  registered = true;
  registerMessages("fr", FR);
  registerMessages("ar", AR);
}
